import React, { ReactElement } from 'react';
import classNames from 'classnames';

type Option<T> = {
    displayName: string;
    value: T;
    optionClass?: string;
};

export interface ToggleButtonRef {
    value: () => any;
}

export interface ToggleButtonProps<T> {
    className?: string;
    wrapperClassName?: string;
    value: T;
    groupOptions: Array<Option<T>>;
    onChange: (value: T) => void;
    label?: string;
}

interface MultiToggleProps<T> {
    className?: string;
    wrapperClassName?: string;
    selectedOption: T;
    options: Array<Option<T>>;
    onSelectOption: (value: T) => void;
    label?: string;
}

type MultiToggleComponent =
    <T>(props: MultiToggleProps<T>) => ReactElement<MultiToggleProps<T>> | null;

const MultiToggle: MultiToggleComponent = ({
    selectedOption,
    options,
    onSelectOption,
    wrapperClassName,
    className,
    label,
}) => {
    // If required variables aren't passed, return empty
    if (!options) return null;

    const numOptions = options.length;

    const columnWidth = numOptions ? 100 / numOptions : numOptions;

    const isSelectedOption = (option: (typeof options)[number]) => option.value === selectedOption;
    const getSelectedIndex = () => {
        const indexFound = options.findIndex((option) => isSelectedOption(option));
        return indexFound > -1 ? indexFound : 0;
    };

    const createToggleOption = (option: (typeof options)[number], index: number) => {
        const selectOption = () => onSelectOption(option.value);

        const optionClasss = classNames('toggleOption', {
            selected: isSelectedOption(option),
        });
        const optionStyle = {
            width: `${columnWidth}%`,
        };

        return (
            <button
                key={index}
                type="button"
                onClick={selectOption}
                className={optionClasss}
                style={optionStyle}
            >
                {option.displayName || option.value}
            </button>
        );
    };

    const toggleClass = classNames('toggleContainer', className);
    const toggleStyle = {
        width: `${columnWidth}%`,
        transform: `translateX(${100 * getSelectedIndex()}%)`,
        WebkitTransform: `translateX(${100 * getSelectedIndex()}%)`,
        MozTransform: `translateX(${100 * getSelectedIndex()}%)`,
        msTransform: `translateX(${100 * getSelectedIndex()}%)`,
    };

    const selectedToggleClass = classNames(
        'toggle',
        options[getSelectedIndex()].optionClass,
        {
            hidden: selectedOption === null,
        },
    );

    const renderLabel = label ? <span className="toggle-label">{label}</span> : null;
    return (
        <div className={classNames('toggle-wrapper', wrapperClassName)}>
            {renderLabel}
            <div className={toggleClass}>
                {options.map(createToggleOption)}
                <div className={selectedToggleClass} style={toggleStyle} />
            </div>
        </div>
    );
};

type ToggleButtonComponent = <T>(props: ToggleButtonProps<T>) => ReactElement | null;

export const ToggleButton: ToggleButtonComponent = (props) => {
    const {
        wrapperClassName,
        className,
        value,
        onChange,
        groupOptions,
        label,
    } = props;

    return (
        <MultiToggle
            wrapperClassName={wrapperClassName}
            className={className}
            options={groupOptions}
            selectedOption={value}
            onSelectOption={onChange}
            label={label}
        />
    );
};
